import { isReportableEnvironment } from "@/utils/isReportableEnvironment";
import { telemetrySendPost } from "@/utils/monitoring/telemetrySender";
import { BreezeChannelIdentifier } from "@microsoft/applicationinsights-core-js";
import type { IConfig, IConfiguration } from "@microsoft/applicationinsights-web";

// Domains that must NOT receive Request-Id/traceparent correlation headers when
// enableCorsCorrelation is on — third parties outside our backend's trust boundary. Listed as
// exact hosts, not a "*.ibabs.eu" wildcard: the SDK does an unanchored substring match, and the
// prod backend (backend.debrief.ibabs.eu) is itself a *.ibabs.eu host that must stay correlated.
const CORRELATION_HEADER_EXCLUDED_DOMAINS = ["signon.ibabs.eu", "signon-test.ibabs.eu", "portal.ibabs.eu", "portal0100test.ibabs.eu", "*.productfruits.com"];

// The SDK requires *a* value here to initialize, but it's never actually used: telemetry is
// sent via httpXHROverride to our own backend proxy (telemetrySender.ts), which rewrites each
// envelope's iKey to the real Application Insights key server-side. Verified against the SDK's
// own source (ConnectionStringParser) that this value is never format-validated.
const PLACEHOLDER_INSTRUMENTATION_KEY = "browser-proxy";

// undefined (no-op) outside BETA/QA/PROD — same gate as AppLogger.ts's own reporting, so RUM
// and business-event reporting are always enabled/disabled together. See docs/application-insights-rum.md.
export const getAppInsightsConfig = (): (IConfiguration & IConfig) | undefined => {
  if (!isReportableEnvironment()) return undefined;

  return {
    instrumentationKey: PLACEHOLDER_INSTRUMENTATION_KEY,
    // httpXHROverride belongs to the Sender/channel plugin's own config, not the top-level
    // IConfiguration — it's read via extensionConfig[BreezeChannelIdentifier], per the SDK's
    // own extension-config pattern (applicationinsights-channel-js's Sender.js).
    // alwaysUseXhrOverride is required too: without it, Sender.js only falls back to
    // httpXHROverride when its own native Beacon/XHR/Fetch senders fail to resolve — which in
    // a real browser they always do, so the override is silently ignored and telemetry goes
    // straight to Azure's default endpoint with our placeholder key (400). Confirmed against
    // Sender.js: `httpInterface = _alwaysUseCustomSend ? customInterface : (httpInterface || customInterface || xhrInterface)`.
    extensionConfig: {
      [BreezeChannelIdentifier]: {
        httpXHROverride: { sendPOST: telemetrySendPost },
        alwaysUseXhrOverride: true,
      },
    },
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    correlationHeaderExcludedDomains: CORRELATION_HEADER_EXCLUDED_DOMAINS,
    disableCookiesUsage: true,
    autoTrackPageVisitTime: true,
    enableUnhandledPromiseRejectionTracking: true,
    // Must stay true - see "Unload-time delivery" in docs/application-insights-rum.md.
    disableFlushOnBeforeUnload: true,
    // Offline storage and retries
    enableSessionStorageBuffer: true,
    namePrefix: "SmartDebrief",
  };
};
